import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';
import { useStudioContext } from '../../../../contexts/StudioContext';
import { useStudioPermissions } from '../../../../hooks/useStudioPermissions';
import {
  Studio,
  StudioRole,
  StudioPermissions,
} from '../../../../types/studio';
import StudioTabNavigation from './StudioTabNavigation';

/**
 * Studio header component that displays current studio context and provides
 * studio-specific navigation and actions.
 */
interface StudioHeaderProps {
  /** Optional CSS class name */
  className?: string;
  /** Whether to show studio action buttons */
  showActions?: boolean;
  /** Studio data to display (if provided, will use this instead of context) */
  studio?: Studio | null;
  /** User's role in the studio (if studio prop is provided) */
  userRole?: StudioRole | null;
  /** User's permissions in the studio (if studio prop is provided) */
  permissions?: StudioPermissions | null;
  /** Loading state (if studio prop is provided) */
  loading?: boolean;
}

const StudioHeader: React.FC<StudioHeaderProps> = ({
  className,
  showActions = true,
  studio: propStudio,
  userRole: _propUserRole,
  permissions: propPermissions,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Always call hooks (React rules), but determine which data to use
  const { currentStudio: contextStudio } = useStudioContext();
  const { permissions: contextPermissions } = useStudioPermissions();

  // Determine which studio data to use - prioritize props over context
  const studio = propStudio !== undefined ? propStudio : contextStudio;
  const usingProps = propStudio !== undefined;

  // Show loading state if explicitly loading
  if (loading) {
    return null; // Could add a loading skeleton here if needed
  }

  // Don't render if no studio data
  if (!studio) {
    return null;
  }

  const getStudioInitials = (name: string) =>
    name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);

  // Use prop permissions if provided, otherwise use backend permissions from context
  const permissions: StudioPermissions | null = usingProps
    ? (propPermissions ?? null)
    : contextPermissions;

  return (
    <Box className={className}>
      <AppBar
        position="static"
        elevation={1}
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Studio Info Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0, // Allow text truncation
            }}
          >
            {/* Studio Avatar */}
            <Avatar
              sx={{
                width: 40,
                height: 40,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                mr: 2,
                fontSize: '1rem',
                fontWeight: 600,
              }}
              src={studio.logo}
            >
              {getStudioInitials(studio.name)}
            </Avatar>

            {/* Studio Name and Info */}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {studio.name}
              </Typography>

              {!isMobile && studio.memberCount !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={`${studio.memberCount} members`}
                    icon={<PeopleIcon />}
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      '& .MuiChip-icon': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Studio Tab Navigation */}
      <StudioTabNavigation
        studio={studio}
        permissions={permissions}
        show={showActions}
      />
    </Box>
  );
};

export default StudioHeader;
